import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { CHANNEL, colorToolSlugs } from '@boss/constants/b2b-b2c';
import { contentfulContext } from '@boss/contentful-client';
import { content as contentService } from '@boss/services';
import { Environment } from '@boss/types/b2b-b2c';

import { SEO } from '../../components';
import { IS_BUILD, PageTypes } from '../../constants';
import { ColorToolOverviewPage } from '../../containers';
import Layout from '../../layout/default';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapNextToContentfulLocaleMarket, mapNextToContentfulMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';

export const config = {
  maxDuration: 30,
};

export const getStaticProps = async ({ preview, locale }: GetStaticPropsContext) => {
  if (!locale || locale === 'default') {
    return {
      notFound: true,
    };
  }

  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);
  const market = mapNextToContentfulMarket(locale);

  if (!contentfulLocaleMarket || !market) {
    return {
      notFound: true,
    };
  }

  // SET GLOBAL MARKET CONTEXT
  contentfulContext.market = market;

  const [navigation, colorToolOverviewPage] = await Promise.all([
    getMappedNavigation(contentfulLocaleMarket, !!preview),
    contentService.getOverviewPage({
      locale: contentfulLocaleMarket,
      channel: CHANNEL,
      type: 'Color tool',
      batchMode: IS_BUILD,
      preview: !!preview,
      market,
    }),
  ]);

  if (!navigation) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      pageType: PageTypes.COLOR_TOOL,
      locale,
      preview: !!preview,
      navigation,
      colorToolOverviewPage,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      ...(await getServerSideTranslations(locale, ['common', 'stores', 'forms', 'colorTool'])),
    },
  };
};

export const Index = ({
  navigation,
  preview,
  environment,
  locale,
  colorToolOverviewPage,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation();
  const pageTitle = t('overviewTitle', { ns: 'colorTool' });
  const pageDescription = colorToolOverviewPage?.pageTitle || pageTitle;
  const crumbles = [{ href: colorToolSlugs[locale], label: pageTitle }];

  return (
    <SEO
      breadcrumbs={crumbles}
      description={pageDescription}
      imageSrc={colorToolOverviewPage?.ogImage?.image?.file?.url}
      localizedSlugs={colorToolSlugs}
      navigation={navigation}
      title={pageTitle}
    >
      <Layout
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={colorToolSlugs}
        navigation={navigation}
        preview={preview}
      >
        <ColorToolOverviewPage locale={locale} page={colorToolOverviewPage} />
      </Layout>
    </SEO>
  );
};

export default Index;
